import React, { Fragment, type ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  parseChatBlocks,
  parseChatInline,
  type ChatList,
} from './chat-markdown.helpers';

const useStyles = makeStyles()({
  // Sections stand apart: more room above a heading than inside a section.
  root: {
    '& > * + *': { marginTop: 8 },
    '& > * + [data-heading]': { marginTop: 18 },
    '& > [data-heading] + *': { marginTop: 4 },
  },
  block: { margin: 0 },
  heading: { margin: 0, fontWeight: 600 },
  list: {
    margin: 0,
    paddingLeft: 18,
    '& ul, & ol': { marginTop: 2 },
    '& li + li': { marginTop: 4 },
  },
});

type Classes = ReturnType<typeof useStyles>['classes'];

const text = (value: string): ReactNode =>
  parseChatInline(value).map((part, index) =>
    part.bold ? (
      <strong key={index}>{part.text}</strong>
    ) : (
      <Fragment key={index}>{part.text}</Fragment>
    ),
  );

const renderList = (
  list: ChatList,
  classes: Classes,
  key?: number,
): ReactNode => {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag key={key} className={classes.list}>
      {list.items.map((item, index) => (
        <li key={index}>
          {text(item.text)}
          {item.children.map((child, childIndex) =>
            renderList(child, classes, childIndex),
          )}
        </li>
      ))}
    </Tag>
  );
};

type ChatMarkdownProps = {
  content: string;
  /** Shown inline at the end of the last paragraph, or after the blocks. */
  trailing?: ReactNode;
};

/**
 * An agent message rendered from Markdown as React elements, so the text
 * can never inject HTML. Text without Markdown reads as plain lines.
 */
export const ChatMarkdown: React.FC<ChatMarkdownProps> = ({
  content,
  trailing,
}) => {
  const { classes } = useStyles();
  const blocks = parseChatBlocks(content);
  const trailingInline = blocks.at(-1)?.kind === 'paragraph';
  return (
    <div className={classes.root}>
      {blocks.map((block, index) => {
        switch (block.kind) {
          case 'heading':
            return (
              <p key={index} className={classes.heading} data-heading>
                {text(block.text)}
              </p>
            );
          case 'list':
            return renderList(block.list, classes, index);
          case 'paragraph':
            return (
              <p key={index} className={classes.block}>
                {block.lines.map((line, lineIndex) => (
                  <Fragment key={lineIndex}>
                    {lineIndex > 0 && <br />}
                    {text(line)}
                  </Fragment>
                ))}
                {index === blocks.length - 1 && trailing}
              </p>
            );
        }
      })}
      {trailing && !trailingInline && <div>{trailing}</div>}
    </div>
  );
};
